/**
 * Reads behind the admin's remainder notices (COL-483). Kept apart from the
 * pure helpers in src/lib/offset-remainder.ts, which stay testable without a
 * Supabase client.
 */
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

/**
 * The number of the OPEN remainder an offset invoice's close opened, if any.
 * The DB opens it in the same write as the close
 * (20260925140100_offset_remainder_invoices.sql), so by the time the payout or
 * send has returned it is there to read. Only one still open (unsent and
 * unpaid) counts: the notice says it now bills the rest. NULL when there is
 * none, or the read fails in any way: it only feeds a notice, and callers
 * fire it without waiting, so it must never reject.
 */
export async function fetchOpenRemainderNo(
  invoiceId: string
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from("management_invoices")
      .select("invoice_no")
      .eq("remainder_of", invoiceId)
      .in("status", ["pending", "overdue"])
      .is("sent_at", null)
      .maybeSingle();
    if (error) return null;
    return (data?.invoice_no as string | undefined) ?? null;
  } catch {
    return null;
  }
}

/**
 * COL-483: an offset invoice closed (paid out or sent) while the month's rent
 * was still coming in opened a REMAINDER for the rest. Say so, so the admin
 * knows the month is not settled yet.
 */
export function notifyRemainderOpened(
  invoiceNo: string,
  t: (key: string, fallback?: string) => string
): void {
  toast.info(
    t(
      "admin.payments.toastRemainderOpened",
      "Rent for this month is still coming in, so remainder invoice {invoice} now bills the rest."
    ).replace("{invoice}", invoiceNo)
  );
}
